import { useState, useMemo } from "react";
import { useRoute, Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BeautyChatBot } from "@/components/BeautyChatBot";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ShoppingCart,
  Package,
  Search,
  Plus,
  Minus,
  Trash2,
  ArrowLeft,
  Phone,
  CheckCircle2,
  Loader2,
  Store,
  Instagram,
  MessageCircle,
  Facebook,
  Calendar,
  ChevronLeft,
  Tag,
  Box,
} from "lucide-react";

interface PublicStoreProduct {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  brand?: string | null;
  salePrice: string | number;
  imageUrl?: string | null;
  unit?: string | null;
  stockQty?: string | number | null;
}

interface CartItem {
  productId: number;
  name: string;
  quantity: number;
  unitPrice: number;
  imageUrl?: string | null;
}

interface SalonStorePageProps {
  /** Usado apenas pela resolução de domínio próprio já verificado. */
  salonSlug?: string;
  isCustomStoreDomain?: boolean;
}

export default function SalonStorePage({ salonSlug, isCustomStoreDomain = false }: SalonStorePageProps) {
  const [, params] = useRoute("/s/:slug/loja");
  const slug = salonSlug ?? params?.slug ?? "";
  const publicSalonPath = isCustomStoreDomain ? "/" : `/s/${slug}`;

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Todos");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PublicStoreProduct | null>(null);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [success, setSuccess] = useState(false);
  const [submittedOrderId, setSubmittedOrderId] = useState<number | null>(null);

  const { data: products, isLoading } = trpc.products.listPublic.useQuery(
    { salonSlug: slug },
    { enabled: !!slug }
  );

  const { data: salonInfo } = (trpc as any).products.getSalonIdBySlug.useQuery(
    { salonSlug: slug },
    { enabled: !!slug }
  );

  const { data: salonData } = trpc.salon.getBySlug.useQuery(
    { slug },
    { enabled: !!slug }
  );
  const createOrder = (trpc as any).storeOrders.create.useMutation({
    onSuccess: (result: { orderId: number }) => {
      setSubmittedOrderId(result.orderId);
      setSuccess(true);
      setCart([]);
    },
    onError: (err: any) => {
      toast.error("Erro ao enviar pedido: " + err.message);
    },
  });

  const categories = useMemo(() => {
    if (!products) return ["Todos"];
    const cats = Array.from(new Set(products.map((p: any) => p.category ?? "Outros").filter(Boolean)));
    return ["Todos", ...cats.sort()];
  }, [products]);

  const filtered = useMemo(() => {
    if (!products) return [];
    return products.filter((p: any) => {
      const matchSearch =
        search === "" ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.brand ?? "").toLowerCase().includes(search.toLowerCase());
      const matchCat =
        categoryFilter === "Todos" || (p.category ?? "Outros") === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [products, search, categoryFilter]);

  const salonName = products?.[0]?.salonName ?? slug;

  const cartTotal = cart.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  function getAvailableStock(product: PublicStoreProduct) {
    return Math.max(0, Math.floor(Number(product.stockQty ?? 0)));
  }

  function isValidStorePhone(phone: string) {
    const digits = phone.replace(/\D/g, "");
    const localNumber =
      (digits.length === 12 || digits.length === 13) && digits.startsWith("55")
        ? digits.slice(2)
        : digits;
    return /^[1-9]{2}[2-9]\d{7,8}$/.test(localNumber);
  }

  function addToCart(p: PublicStoreProduct) {
    const availableStock = getAvailableStock(p);
    const quantityInCart = cart.find((item) => item.productId === p.id)?.quantity ?? 0;
    if (availableStock <= quantityInCart) {
      toast.error(availableStock === 0 ? "Este produto está indisponível no momento." : "Você já adicionou todas as unidades disponíveis.");
      return;
    }
    setCart((prev) => {
      const existing = prev.find((i: any) => i.productId === p.id);
      if (existing) {
        return prev.map((i: any) =>
          i.productId === p.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          productId: p.id,
          name: p.name,
          quantity: 1,
          unitPrice: Number(p.salePrice),
          imageUrl: p.imageUrl,
        },
      ];
    });
    toast.success(`${p.name} adicionado ao carrinho`);
  }

  function updateQty(productId: number, delta: number) {
    const product = products?.find((item: PublicStoreProduct) => item.id === productId);
    const availableStock = product ? getAvailableStock(product) : 0;
    const quantityInCart = cart.find((item) => item.productId === productId)?.quantity ?? 0;
    if (delta > 0 && quantityInCart >= availableStock) {
      toast.error(availableStock === 0 ? "Este produto está indisponível no momento." : "Quantidade limitada ao estoque disponível.");
      return;
    }
    setCart((prev) =>
      prev
        .map((i: any) => (i.productId === productId ? { ...i, quantity: Math.min(i.quantity + delta, availableStock) } : i))
        .filter((i: any) => i.quantity > 0)
    );
  }

  function handleSubmit() {
    if (!clientName.trim()) {
      toast.error("Informe seu nome para continuar.");
      return;
    }
    if (!isValidStorePhone(clientPhone)) {
      toast.error("Informe seu WhatsApp com DDD para enviar o pedido.");
      return;
    }
    if (!salonInfo?.salonId) {
      toast.error("Salão não encontrado.");
      return;
    }
    createOrder.mutate({
      salonId: salonInfo.salonId,
      clientName: clientName.trim(),
      clientPhone: clientPhone.trim(),
      items: cart.map((i: any) => ({
        productId: i.productId,
        quantity: i.quantity,
      })),
      notes: notes.trim() || undefined,
    });
  }

  // ── Customizações dinâmicas ────────────────────────────────────────────────
  const sd = salonData as any;
  const themeColor     = sd?.themeColor     || "#C97D2E";
  const themeBgColor   = sd?.themeBgColor   || "#F5F0E8";
  const themeDarkColor = sd?.themeDarkColor || "#2C1810";
  const ctaButtonText  = sd?.ctaButtonText  || "Agendar agora";
  const storeCtaButtonText = sd?.storeCtaButtonText?.trim() || ctaButtonText || "Peça já";
  const bannerUrl      = sd?.bannerUrl      || null;
  const instagramUrl   = sd?.instagramUrl   || null;
  const facebookUrl    = sd?.facebookUrl    || null;
  const whatsappUrl    = sd?.whatsappUrl    || (salonData?.phone ? `https://wa.me/${salonData.phone.replace(/\D/g, "")}` : null);
  const storeCtaDestination = sd?.storeCtaButtonUrl?.trim() || whatsappUrl || `/booking?salon=${salonInfo?.salonId}`;
  const storeCtaIsExternal = /^https?:\/\//i.test(storeCtaDestination);
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: themeBgColor, color: themeDarkColor }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur border-b" style={{ backgroundColor: `${themeBgColor}f0`, borderColor: `${themeColor}33` }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link href={publicSalonPath}>
              <Button variant="ghost" size="sm" className="gap-1.5" style={{ color: `${themeDarkColor}99` }}>
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Voltar</span>
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5" style={{ color: themeColor }} />
              <div>
                <h1 className="font-semibold text-sm leading-tight" style={{ color: themeDarkColor }}>{salonName}</h1>
                <p className="text-xs" style={{ color: `${themeDarkColor}88` }}>Loja de Produtos</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {storeCtaIsExternal ? (
              <a href={storeCtaDestination} target="_blank" rel="noopener noreferrer">
                <Button size="sm" style={{ backgroundColor: themeColor, color: "#fff" }} className="hidden sm:flex gap-1.5">
                  <MessageCircle className="w-4 h-4" />
                  {storeCtaButtonText}
                </Button>
              </a>
            ) : (
              <Link href={storeCtaDestination}>
                <Button size="sm" style={{ backgroundColor: themeColor, color: "#fff" }} className="hidden sm:flex gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {storeCtaButtonText}
                </Button>
              </Link>
            )}
            <Button
              variant="outline"
              size="sm"
              className="relative gap-2"
              style={{ borderColor: `${themeColor}66`, color: themeColor }}
              onClick={() => setShowCart(true)}
              disabled={cart.length === 0}
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="hidden sm:inline">Carrinho</span>
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" style={{ backgroundColor: themeColor }}>
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Mini banner hero da loja */}
      {bannerUrl && (
        <div className="relative h-32 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bannerUrl})` }} />
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-center text-white">
              <h2 className="text-xl font-bold">{salonName}</h2>
              <p className="text-sm text-white/70">Loja de Produtos</p>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: `${themeDarkColor}66` }} />
            <Input
              placeholder="Buscar produtos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              style={{ borderColor: `${themeColor}33`, color: themeDarkColor }}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat: any) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                style={categoryFilter === cat
                  ? { backgroundColor: themeColor, color: "#fff", borderColor: themeColor }
                  : { backgroundColor: "transparent", color: `${themeDarkColor}88`, borderColor: `${themeColor}44` }
                }
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Products grid */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: themeColor }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-12 h-12 mb-3" style={{ color: `${themeDarkColor}33` }} />
            <p style={{ color: `${themeDarkColor}88` }}>Nenhum produto encontrado.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4">
            {filtered.map((p: any) => {
              const inCart = cart.find((i: any) => i.productId === p.id);
              const availableStock = getAvailableStock(p);
              const isOutOfStock = availableStock === 0;
              const isLastUnits = availableStock > 0 && availableStock <= 3;
              return (
                <Card
                  key={p.id}
                  className="group overflow-hidden rounded-2xl border shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ backgroundColor: "#fff", borderColor: `${themeColor}22` }}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedProduct(p)}
                    className="relative block aspect-[4/5] w-full overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                    style={{ backgroundColor: `${themeColor}0d`, outlineColor: themeColor }}
                    aria-label={`Ver detalhes de ${p.name}`}
                  >
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        alt={p.name}
                        className="h-full w-full object-cover transition-transform duration-300 motion-reduce:transition-none sm:object-contain group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Package className="h-12 w-12" style={{ color: `${themeColor}44` }} />
                      </div>
                    )}
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent px-3 pb-2 pt-8 text-xs font-medium text-white opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                      Ver detalhes
                    </span>
                    {inCart && (
                      <span className="absolute right-2 top-2">
                        <Badge className="text-xs text-white shadow-sm" style={{ backgroundColor: themeColor }}>
                          {inCart.quantity} no carrinho
                        </Badge>
                      </span>
                    )}
                    {isOutOfStock && (
                      <span className="absolute left-2 top-2">
                        <Badge variant="secondary" className="bg-white/95 text-xs font-semibold text-muted-foreground shadow-sm">
                          Indisponível
                        </Badge>
                      </span>
                    )}
                  </button>
                  <CardContent className="space-y-2 p-3 sm:p-3.5">
                    <button
                      type="button"
                      onClick={() => setSelectedProduct(p)}
                      className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      style={{ outlineColor: themeColor }}
                    >
                      <p className="line-clamp-2 text-sm font-semibold leading-tight" style={{ color: themeDarkColor }}>{p.name}</p>
                    </button>
                    {p.brand && (
                      <p className="text-xs" style={{ color: `${themeDarkColor}66` }}>{p.brand}</p>
                    )}
                    <p className="text-lg font-bold leading-none" style={{ color: themeColor }}>
                      R$ {Number(p.salePrice).toFixed(2).replace(".", ",")}
                    </p>
                    {isLastUnits && (
                      <p className="text-xs font-medium" style={{ color: themeColor }}>
                        Últimas {availableStock} {availableStock === 1 ? "unidade" : "unidades"}
                      </p>
                    )}
                    <Button
                      className="h-10 w-full gap-1.5 text-sm font-semibold text-white"
                      style={{ backgroundColor: themeColor }}
                      onClick={(event) => {
                        event.stopPropagation();
                        addToCart(p);
                      }}
                      disabled={isOutOfStock}
                    >
                      <Plus className="h-4 w-4" />
                      {isOutOfStock ? "Indisponível" : inCart ? "Adicionar mais" : "Adicionar"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Floating cart button (mobile) */}
        {cartCount > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
            <Button
              size="lg"
              className="shadow-xl gap-2 px-6 text-white"
              style={{ backgroundColor: themeColor }}
              onClick={() => setShowCart(true)}
            >
              <ShoppingCart className="w-5 h-5" />
              Ver carrinho · R$ {cartTotal.toFixed(2).replace(".", ",")}
              <Badge className="text-xs ml-1" style={{ backgroundColor: "rgba(255,255,255,0.25)", color: "#fff" }}>{cartCount}</Badge>
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t py-6 text-center text-sm" style={{ borderColor: `${themeColor}33`, color: `${themeDarkColor}66` }}>
        {(instagramUrl || facebookUrl || whatsappUrl) && (
          <div className="flex gap-4 justify-center mb-3">
            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity" style={{ color: themeColor }}>
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {facebookUrl && (
              <a href={facebookUrl} target="_blank" rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity" style={{ color: themeColor }}>
                <Facebook className="w-5 h-5" />
              </a>
            )}
            {whatsappUrl && (
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity" style={{ color: themeColor }}>
                <MessageCircle className="w-5 h-5" />
              </a>
            )}
          </div>
        )}
        <p>
          Loja online por{" "}
          <a href={isCustomStoreDomain ? "https://belezaebronze.club" : "/"} className="hover:underline font-medium" style={{ color: themeColor }}>Beleza &amp; Bronze</a>
        </p>
      </footer>

      {/* Product details — full screen on mobile, split panel on larger screens */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        {selectedProduct && (() => {
          const selectedInCart = cart.find((item) => item.productId === selectedProduct.id);
          const selectedAvailableStock = getAvailableStock(selectedProduct);
          const selectedIsOutOfStock = selectedAvailableStock === 0;
          const productMeta = [
            selectedProduct.category && { label: "Categoria", value: selectedProduct.category, icon: Tag },
            selectedProduct.brand && { label: "Marca", value: selectedProduct.brand, icon: Store },
            selectedProduct.unit && { label: "Unidade", value: selectedProduct.unit, icon: Box },
          ].filter(Boolean) as Array<{ label: string; value: string; icon: typeof Tag }>;

          return (
            <DialogContent
              showCloseButton={false}
              className="gap-0 overflow-hidden border-0 p-0 max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:h-[100dvh] max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none sm:max-w-4xl sm:border"
            >
              <div className="flex h-full min-h-0 flex-col sm:grid sm:grid-cols-[minmax(0,1.1fr)_minmax(20rem,0.9fr)]">
                <div className="relative shrink-0 overflow-hidden bg-muted/40 max-sm:aspect-[4/5] sm:min-h-[540px]">
                  {selectedProduct.imageUrl ? (
                    <img
                      src={selectedProduct.imageUrl}
                      alt={selectedProduct.name}
                      className="h-full w-full object-cover sm:object-contain"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Package className="h-20 w-20" style={{ color: `${themeColor}44` }} />
                    </div>
                  )}
                  <DialogClose asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="secondary"
                      className="absolute left-3 top-3 h-10 w-10 rounded-full bg-white/95 shadow-md hover:bg-white sm:left-auto sm:right-3"
                      aria-label="Voltar para a loja"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                  </DialogClose>
                  {selectedProduct.category && (
                    <Badge className="absolute bottom-3 left-3 border-0 bg-black/70 px-2.5 py-1 text-xs text-white">
                      {selectedProduct.category}
                    </Badge>
                  )}
                </div>

                <div className="flex min-h-0 flex-1 flex-col bg-white">
                  <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-5 sm:px-7 sm:pt-8">
                    <DialogHeader className="items-start text-left">
                      {selectedProduct.brand && (
                        <DialogDescription className="font-medium" style={{ color: themeColor }}>
                          {selectedProduct.brand}
                        </DialogDescription>
                      )}
                      <DialogTitle className="pr-8 text-2xl leading-tight sm:text-3xl" style={{ color: themeDarkColor }}>
                        {selectedProduct.name}
                      </DialogTitle>
                    </DialogHeader>

                    <p className="mt-5 text-3xl font-bold tracking-tight" style={{ color: themeColor }}>
                      R$ {Number(selectedProduct.salePrice).toFixed(2).replace(".", ",")}
                    </p>

                    <div className="mt-5 border-y py-4" style={{ borderColor: `${themeColor}20` }}>
                      <p className="text-sm font-semibold" style={{ color: themeDarkColor }}>Sobre este produto</p>
                      <p className="mt-2 whitespace-pre-line text-sm leading-6" style={{ color: `${themeDarkColor}bb` }}>
                        {selectedProduct.description?.trim() || "Não há uma descrição adicional cadastrada para este produto."}
                      </p>
                    </div>

                    <div className="mt-5 grid gap-2">
                      {productMeta.map(({ label, value, icon: Icon }) => (
                        <div key={label} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ backgroundColor: `${themeColor}0d` }}>
                          <Icon className="h-4 w-4 shrink-0" style={{ color: themeColor }} />
                          <span className="text-xs" style={{ color: `${themeDarkColor}88` }}>{label}</span>
                          <span className="ml-auto text-sm font-medium" style={{ color: themeDarkColor }}>{value}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ backgroundColor: `${themeColor}0d` }}>
                        <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: themeColor }} />
                        <span className="text-xs" style={{ color: `${themeDarkColor}88` }}>Disponibilidade</span>
                        <span className="ml-auto text-sm font-medium" style={{ color: themeDarkColor }}>
                          {selectedIsOutOfStock
                            ? "Indisponível"
                            : selectedAvailableStock <= 3
                              ? `Últimas ${selectedAvailableStock} ${selectedAvailableStock === 1 ? "unidade" : "unidades"}`
                              : "Em estoque"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t bg-white p-4 shadow-[0_-8px_24px_rgba(0,0,0,0.05)]" style={{ borderColor: `${themeColor}20` }}>
                    <Button
                      className="h-12 w-full gap-2 text-base font-semibold text-white"
                      style={{ backgroundColor: themeColor }}
                      onClick={() => addToCart(selectedProduct)}
                      disabled={selectedIsOutOfStock}
                    >
                      <Plus className="h-5 w-5" />
                      {selectedIsOutOfStock ? "Indisponível" : selectedInCart ? "Adicionar mais ao carrinho" : "Adicionar ao carrinho"}
                    </Button>
                    {selectedInCart && (
                      <p className="mt-2 text-center text-xs" style={{ color: `${themeDarkColor}77` }}>
                        Você já tem {selectedInCart.quantity} {selectedInCart.quantity === 1 ? "unidade" : "unidades"} no carrinho.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </DialogContent>
          );
        })()}
      </Dialog>

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              Seu Carrinho
            </DialogTitle>
          </DialogHeader>
          {cart.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Carrinho vazio.</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {cart.map((item: any) => {
                const stockProduct = products?.find((product: PublicStoreProduct) => product.id === item.productId);
                const availableStock = stockProduct ? getAvailableStock(stockProduct) : 0;
                const cannotAddMore = item.quantity >= availableStock;
                return (
                  <div key={item.productId} className="flex items-center gap-3">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} className="w-12 h-12 rounded-md object-cover border border-border/40 shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-md bg-muted/50 border border-border/30 flex items-center justify-center shrink-0">
                        <Package className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">R$ {item.unitPrice.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQty(item.productId, -1)}>
                        {item.quantity === 1 ? <Trash2 className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                      </Button>
                      <span className="text-sm font-medium w-5 text-center">{item.quantity}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0"
                        onClick={() => updateQty(item.productId, 1)}
                        disabled={cannotAddMore}
                        aria-label={`Adicionar mais ${item.name}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <span className="text-sm font-semibold w-16 text-right shrink-0">
                      R$ {(item.quantity * item.unitPrice).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {cart.length > 0 && (
            <>
              <div className="flex items-center justify-between pt-2 border-t border-border/30">
                <span className="font-semibold">Total</span>
                <span className="text-lg font-bold text-primary">
                  R$ {cartTotal.toFixed(2).replace(".", ",")}
                </span>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCart(false)}>Continuar comprando</Button>
                <Button onClick={() => { setShowCart(false); setShowCheckout(true); }}>
                  Finalizar pedido
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md">
          {success ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <CheckCircle2 className="w-14 h-14 text-green-500" />
              <h2 className="text-xl font-bold">Pedido enviado!</h2>
              <p className="text-sm text-muted-foreground">
                {submittedOrderId ? `Pedido #${submittedOrderId} registrado. ` : ""}
                O salão entrará em contato para confirmar a disponibilidade e combinar a entrega ou retirada.
              </p>
              <Button onClick={() => { setShowCheckout(false); setSuccess(false); setSubmittedOrderId(null); }}>
                Continuar comprando
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Finalizar Pedido</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-3 space-y-1.5 text-sm">
                  {cart.map((i: any) => (
                    <div key={i.productId} className="flex justify-between">
                      <span className="text-muted-foreground">{i.quantity}x {i.name}</span>
                      <span className="font-medium">R$ {(i.quantity * i.unitPrice).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold pt-1 border-t border-border/30">
                    <span>Total</span>
                    <span className="text-primary">R$ {cartTotal.toFixed(2).replace(".", ",")}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Seu nome *</Label>
                  <Input
                    placeholder="Como podemos te chamar?"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" /> WhatsApp / Telefone *
                  </Label>
                  <Input
                    placeholder="(00) 00000-0000"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    inputMode="tel"
                    autoComplete="tel"
                    aria-describedby="store-phone-hint"
                  />
                  <p id="store-phone-hint" className="text-xs text-muted-foreground">Obrigatório, com DDD, para confirmarmos seu pedido.</p>
                </div>
                <div className="space-y-1">
                  <Label>Observações</Label>
                  <Textarea
                    placeholder="Alguma dúvida ou pedido especial?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="resize-none"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Ao enviar, o salão receberá seu pedido e confirmará disponibilidade e forma de pagamento diretamente com você.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCheckout(false)}>Voltar</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={createOrder.isPending || !clientName.trim() || !isValidStorePhone(clientPhone)}
                >
                  {createOrder.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Enviando...</>
                  ) : "Enviar pedido"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {salonInfo?.salonId && (
        <BeautyChatBot
          salonId={salonInfo.salonId}
          mode="store"
          hasFloatingAction={cartCount > 0}
        />
      )}
    </div>
  );
}
